import { PublicLayout } from '../layout/PublicLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';
import { MermaidDiagram } from '../MermaidDiagram';

export const AboutPage = () => {
  useSEO({
    title: 'How Raisy Works - Confidence-Based Autonomy | Raisy',
    description: 'Deep dive into Raisy\'s recursive confidence system. Learn how AI employees earn trust through performance history. From manual approval to full autonomous execution.',
    keywords: 'confidence scoring, AI workforce, AI employees, process automation, human oversight, enterprise AI, approval management',
    ogTitle: 'How Raisy Works - Confidence-Based Autonomy',
    ogDescription: 'Understand how systems earn the right to run autonomously. The autonomy-compliance paradigm solved.',
    ogImage: 'https://raisy.team/og-about.jpg',
    ogType: 'article',
    canonicalUrl: 'https://raisy.team/about',
  });
  return (
    <PublicLayout>
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {/* Article Header */}
        <div className="mb-12">
          <Badge className="mb-4 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/40">
            Confidence-Based Autonomy
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 dark:text-white mb-4">
            Raisy's Process Orchestration cuts manual intervention from 100% to &lt;5%
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300">
            and maintains enterprise compliance doing it
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-6">
            By the Raisy Team • May 2, 2026
          </p>
        </div>

        {/* Article Content */}
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          {/* The autonomy dilemma */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              The autonomy dilemma
            </h2>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              One of the key challenges of building effective business process automation is teaching systems to execute workflows reliably while maintaining human oversight and control. But traditional automation platforms either push full autonomy (risking compliance violations and costly mistakes) or require humans to approve every action (creating bottlenecks that negate the efficiency gains). This creates an unsolvable tension: organizations need speed without sacrificing safety, yet current platforms force a binary choice between one or the other.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              To overcome this challenge, Raisy introduced a <strong>recursive, hierarchical confidence system</strong> that trains autonomous agents to earn the right to execute independently through proven performance history. Raisy's proprietary <strong>confidence-based autonomy framework</strong> tracks approval rates across three composable levels—Kernels (atomic LLM tasks), Bots (multi-step agents), and Processes (business workflows)—enabling systems to start with mandatory human review and progressively graduate to autonomous execution as they demonstrate consistent reliability.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              Raisy reduces manual approval overhead from 100% to less than 5% while maintaining complete audit trails and compliance certification across all automation tiers. This framework helps organizations build business processes that are not trigger-happy approval-seekers and know when they've earned the autonomy to act independently, enabling the development of efficient and trustworthy automated workflows.
            </p>
          </section>

          {/* The autonomy deficit */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              The autonomy deficit
            </h2>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              Current business automation platforms face what organizations call the "autonomy-compliance paradox." Systems either execute blindly without human oversight (creating regulatory and operational risk) or require humans to manually review and approve every single action, turning automation into a bottleneck that's slower than manual processes.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              This binary choice creates severe operational consequences. Organizations that enforce mandatory human approval on every action find that their "automated" processes aren't actually faster—they're just as slow as manual work, but with added overhead from context-switching and approval queueing. Meanwhile, organizations that push full autonomy find themselves dealing with costly compliance violations, data integrity issues, and angry customers when automation goes wrong without oversight.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              The root problem is structural: current systems don't distinguish between high-risk actions (that demand human review) and low-risk actions (that are safe to automate). They apply the same approval policy uniformly, either approving nothing automatically or approving everything. This all-or-nothing approach destroys both efficiency and safety.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              Traditional process automation solutions attempted to solve the problem at design time by requiring organizations to pre-define which steps are "safe" and which require approval. But this manual classification is error-prone, creates maintenance nightmares when business rules change, and doesn't adapt as system behavior changes over time. The fundamental issue remains: how can a system know when it's ready to be trusted with autonomous execution?
            </p>

            <MermaidDiagram
              id="traditional-approach"
              diagram={`graph TD
A["Traditional Approach:<br/>Manual Classification"]
B["Either Always Approve<br/>or Never Approve"]
C["Compliance Risk ✗<br/>OR Efficiency Loss ✗"]
A -->|Fixed rules| B
B --> C`}
            />

            <p className="text-sm italic text-slate-600 mt-4 text-center">
              Traditional approaches lock in approval policies at design time, forcing binary choices between safety and speed
            </p>
          </section>

          {/* Confidence-based autonomy hierarchy */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              Confidence-based autonomy hierarchy
            </h2>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              To solve the autonomy paradox, Raisy introduced a <strong>three-tier confidence system</strong> that mirrors how humans actually learn trust. Just as a new employee starts with close supervision and gradually earns autonomy through demonstrated performance, Raisy's Kernels, Bots, and Processes start with mandatory human approval and graduate to autonomous execution as their confidence score climbs.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              Raisy computes confidence as a simple, deterministic metric: <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-900 dark:text-slate-100">confidenceScore = (approvals / totalActions) × 100</code>. A Kernel with 100 successful approvals and 0 rejections has a confidence score of 100%. One with 95 approvals and 5 rejections has 95%. The system doesn't require subjective judgment—the confidence score is derived purely from execution history.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              The breakthrough is that Raisy applies this confidence metric across <strong>three independent, composable levels</strong>, creating a recursive architecture where each level follows the same pattern:
            </p>

            <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 my-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white mb-2">Level 1: Kernels (atomic LLM prompts)</h4>
                  <ul className="list-disc list-inside space-y-2 text-slate-700 dark:text-slate-300">
                    <li>Start with mandatory human review</li>
                    <li>After 10+ actions with ≥90% approval rate: can skip review</li>
                    <li>Complete audit trail of every invocation</li>
                    <li>Reusable across all bots and processes</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white mb-2">Level 2: Bots (multi-step agent assemblies)</h4>
                  <ul className="list-disc list-inside space-y-2 text-slate-700 dark:text-slate-300">
                    <li>Composed of Kernels (can have mixed confidence levels)</li>
                    <li>The Bot's confidence score reflects its most conservative kernel</li>
                    <li>Can run autonomously only if all component Kernels are trusted</li>
                    <li>Creates natural "gates" at the composition boundaries</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white mb-2">Level 3: Processes (enterprise workflows)</h4>
                  <ul className="list-disc list-inside space-y-2 text-slate-700 dark:text-slate-300">
                    <li>Composed of Bots (can have mixed confidence levels)</li>
                    <li>Each step can have independent approval requirements</li>
                    <li>Scope-aware: "internal" steps (Bot-to-Bot) can have different policies than "external" steps (customer-facing)</li>
                    <li>Maintains human-in-the-middle for customer interactions even when internal steps are autonomous</li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="text-lg text-slate-700 dark:text-slate-300">
              The elegance of this design is that it's <strong>recursive and self-similar</strong>: the same confidence mechanism works at every level. A process doesn't need special logic to handle mixed-confidence components—it just checks each step's confidence score and applies the appropriate approval gate based on organizational policy.
            </p>

            <MermaidDiagram
              id="confidence-hierarchy"
              diagram={`graph TD
A["Kernel<br/>confidenceScore: 85%"] -->|becomes component of| B["Bot<br/>confidenceScore: min=85%"]
B -->|becomes component of| C["Process<br/>confidenceScore: 85%"]
C -->|can assemble into| D["SuperProcess<br/>confidenceScore: 85%"]`}
            />

            <p className="text-sm italic text-slate-600 mt-4 text-center">
              Confidence cascades through the hierarchy: each level inherits the minimum confidence of its components
            </p>

            <p className="text-lg text-slate-700 dark:text-slate-300">
              This hierarchy creates a powerful implicit capability curriculum. When a new workflow is deployed, all steps start with <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-900 dark:text-slate-100">MANUAL</code> approval mode—humans must review everything. As Kernels and Bots prove themselves through consistent approvals, they automatically graduate to <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-900 dark:text-slate-100">CONFIDENCE_BASED</code> mode. Steps can skip review when their confidence score exceeds the organization's threshold (typically 90%). External-facing steps can require <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-900 dark:text-slate-100">MANUAL</code> review even when internal steps are autonomous, enforcing compliance at the point where it matters most.
            </p>
          </section>

          {/* Scope-aware approval governance */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              Scope-aware approval governance
            </h2>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              To handle the complexity of real business processes, Raisy implements <strong>three approval scopes</strong> that can have different policies:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 dark:text-slate-300 my-4">
              <li><strong>Overall</strong>: Default approval mode for the entire workflow</li>
              <li><strong>Internal</strong>: Approval requirements for Bot-to-Bot communication within the process</li>
              <li><strong>External</strong>: Approval requirements for steps that expose results to customers or outside systems</li>
            </ul>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              An organization might configure:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 dark:text-slate-300 my-4">
              <li>Internal steps: <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-900 dark:text-slate-100">FULL_AUTO</code> (trusted bots run freely)</li>
              <li>External steps: <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-900 dark:text-slate-100">MANUAL</code> (always review before sending to customer)</li>
              <li>Process creation: <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-900 dark:text-slate-100">CONFIDENCE_BASED</code> (trusted bots can assemble new processes, new bots get reviewed)</li>
            </ul>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              This scope-aware design is critical because it captures how enterprises actually manage risk. Not all actions are equally risky. A bot calculating an internal metric (internal scope) can operate autonomously. But the same bot drafting a customer email (external scope) requires review. Raisy lets organizations express these distinctions naturally, without forcing binary approval policies across the entire workflow.
            </p>
          </section>

          {/* Infinite composability */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              Infinite composability
            </h2>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              Unlike rigid automation platforms, Raisy's architecture is <strong>infinitely composable</strong>. Processes can assemble other Processes. A "SuperProcess" that orchestrates multiple sub-processes inherits the confidence score of its least-trusted component, creating natural safety boundaries at every composition level.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300 mt-4">
              This means:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 dark:text-slate-300 my-4">
              <li><strong>Bots can assemble Bots</strong>: A higher-order Bot can delegate sub-tasks to other Bots</li>
              <li><strong>Processes can assemble Processes</strong>: Multi-day workflows can nest other workflows as steps</li>
              <li><strong>Self-assembly</strong>: A "process classifier" Bot can query the registry ("do we have a lead qualification process?"), and if not, auto-create one from available components</li>
              <li><strong>Same rules everywhere</strong>: Every level uses identical confidence tracking, versioning, audit trails, and approval logic</li>
            </ul>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              This recursive design eliminates special cases and exception handlers. The system doesn't need custom logic for "Bot composition" vs "Process composition"—both follow the same pattern. An organization can build arbitrarily complex automation by stacking trusted components, with confidence scores automatically cascading through the hierarchy.
            </p>

            <MermaidDiagram
              id="infinite-composability"
              diagram={`graph TB
K1["Kernel A<br/>90% confidence"]
K2["Kernel B<br/>95% confidence"]
K3["Kernel C<br/>88% confidence"]
B1["Bot 1<br/>88% confidence<br/>K1+K2+K3"]
B2["Bot 2<br/>92% confidence"]
P1["Process A<br/>88% confidence<br/>B1+B2"]
P2["Process B<br/>85% confidence"]
SP["SuperProcess<br/>85% confidence<br/>P1+P2"]

K1 --> B1
K2 --> B1
K3 --> B1
B2 --> P1
B1 --> P1
P1 --> SP
P2 --> SP`}
            />

            <p className="text-sm italic text-slate-600 dark:text-slate-400 mt-4 text-center">
              Confidence cascades through infinite composition: confidence is the minimum of all components
            </p>
          </section>

          {/* Moving from manual to autonomous */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              Moving from manual to autonomous
            </h2>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              For organizations drowning in approval queues, Raisy offers a path forward. Start with mandatory review on everything (100% human-in-the-loop). As Kernels and Bots prove themselves through execution history, they graduate to autonomous execution. Within 2-4 weeks, typical processes see 80-95% reduction in manual approvals while maintaining full compliance.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              The key insight: <strong>autonomy isn't a binary design choice made upfront. It's an earned capability demonstrated through performance history.</strong>
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              Raisy's confidence-based system makes this explicit. Every action is auditable. Every approval decision is logged. And every component has a transparent confidence score based on its track record, not on an administrator's guess about whether it's "ready" to run autonomously.
            </p>
          </section>

          {/* ISO 9001 Compliance */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              ISO 9001 Compliance by Design
            </h2>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              Raisy's architecture is built from the ground up to support <strong>ISO 9001 quality management standards</strong>. Every process execution, approval decision, and system action is automatically logged and auditable, creating the comprehensive documentation trails required by quality frameworks.
            </p>
            <p className="text-lg text-slate-700 dark:text-slate-300 mt-4">
              Key ISO 9001 alignment:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 dark:text-slate-300 my-4">
              <li><strong>Complete audit trails</strong>: Every action logged with timestamp, actor, and outcome</li>
              <li><strong>Document control</strong>: Process versioning and change tracking with immutable history</li>
              <li><strong>Control of records</strong>: All execution logs retained and indexed for retrieval</li>
              <li><strong>Management review</strong>: Automated reporting on process performance and confidence scores</li>
              <li><strong>Continual improvement</strong>: Confidence-based autonomy naturally drives process optimization</li>
              <li><strong>Traceability</strong>: Full parent-child relationships between Kernels → Bots → Processes</li>
            </ul>
            <p className="text-lg text-slate-700 dark:text-slate-300">
              This means organizations deploying Raisy can demonstrate compliance with ISO 9001 requirements immediately, without building separate audit systems or maintaining parallel documentation. The platform <em>is</em> the quality management system.
            </p>
          </section>

          {/* Conclusion */}
          <section>
            <div className="bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 p-8 rounded-lg border border-cyan-200 dark:border-cyan-800">
              <p className="text-lg text-slate-700 dark:text-slate-300 italic">
                "Our approach demonstrates that human oversight and operational efficiency are not a trade-off," Raisy's team explains. "Eliminating unnecessary approvals directly contributes to better compliance, because teams spend oversight effort where it actually matters—on novel or high-stakes actions—rather than rubber-stamping routine operations. This represents a paradigm shift in process automation: from rigid, designed-in approval policies, to adaptive, performance-driven autonomy."
              </p>
            </div>
          </section>
        </div>

        {/* CTA */}
        <div className="mt-16 p-8 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 text-center">
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
            Ready to see it in action?
          </h3>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            Experience confidence-based autonomy with a live demo or request early access.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/app">
              <Button className="bg-cyan-600 hover:bg-cyan-700 gap-2 px-8 text-white">
                Request Invite <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="px-8 dark:text-white dark:border-slate-600 dark:hover:bg-slate-700">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </PublicLayout>
  );
};
